import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Users, Search, Download, Mail, ShieldAlert, Repeat, DollarSign,
  Package, Calendar, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const fmtUsd = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function CustomersTab() {
  const [data, setData] = useState({ customers: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('/creator/customers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { customers: [], summary: null });
    } catch (err) {
      console.error('Failed to load customers:', err);
      toast.error('Could not load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return data.customers;
    const q = search.trim().toLowerCase();
    return data.customers.filter(
      (c) =>
        (c.email || '').toLowerCase().includes(q) ||
        (c.name || '').toLowerCase().includes(q) ||
        (c.products || []).some((p) => p.toLowerCase().includes(q))
    );
  }, [data.customers, search]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('/creator/customers/export', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Customers exported');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const summary = data.summary || {
    total_customers: 0, total_orders: 0, lifetime_revenue: 0, repeat_customers: 0,
  };

  return (
    <div className="space-y-6" data-testid="customers-tab">
      {/* Legal notice */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90">
        <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
        <div>
          <strong className="font-semibold text-amber-100">Use this list responsibly.</strong>{' '}
          These buyers consented only to <em>transactional</em> communication about their
          purchase. Marketing outreach (newsletters, upsells, promos to non-subscribers)
          requires separate opt-in under CASL, GDPR, and CAN-SPAM. Use it for receipts,
          updates, and support — not cold marketing.
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <SummaryCard
          icon={<Users className="h-4 w-4" />}
          label="Customers"
          value={summary.total_customers.toLocaleString()}
          accent="text-[#00F0FF]"
          testId="customers-summary-total"
        />
        <SummaryCard
          icon={<Package className="h-4 w-4" />}
          label="Orders"
          value={summary.total_orders.toLocaleString()}
          accent="text-white"
          testId="customers-summary-orders"
        />
        <SummaryCard
          icon={<DollarSign className="h-4 w-4" />}
          label="Lifetime revenue"
          value={fmtUsd(summary.lifetime_revenue)}
          accent="text-[#00FF94]"
          testId="customers-summary-revenue"
        />
        <SummaryCard
          icon={<Repeat className="h-4 w-4" />}
          label="Repeat buyers"
          value={summary.repeat_customers.toLocaleString()}
          accent="text-[#FF6B6B]"
          testId="customers-summary-repeat"
        />
      </div>

      {/* Search + export bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="customers-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, name, or product…"
            className="bg-neutral-900 border-white/10 pl-9 text-white placeholder:text-white/40"
          />
        </div>
        <Button
          data-testid="customers-export-csv-button"
          onClick={handleExport}
          disabled={exporting || !data.customers.length}
          className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 disabled:opacity-50"
        >
          {exporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export CSV
        </Button>
      </div>

      {/* Customer table */}
      <div
        data-testid="customers-list"
        className="overflow-hidden rounded-xl border border-white/10 bg-neutral-950"
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 text-white/60">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading customers…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-16 text-center" data-testid="customers-empty-state">
            <Users className="mx-auto mb-3 h-10 w-10 text-white/20" />
            <h3 className="text-lg font-semibold text-white">No customers yet</h3>
            <p className="mt-1 text-sm text-white/50">
              {data.customers.length === 0
                ? 'Once buyers complete a purchase, they\'ll appear here.'
                : 'No customers match your search.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900/80 text-xs uppercase tracking-wider text-white/50">
                <tr>
                  <Th>Customer</Th>
                  <Th className="hidden md:table-cell">Products</Th>
                  <Th className="text-center">Orders</Th>
                  <Th className="text-right">LTV</Th>
                  <Th className="hidden sm:table-cell">First</Th>
                  <Th className="hidden sm:table-cell">Last</Th>
                  <Th className="text-right">&nbsp;</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((c) => (
                  <tr
                    key={c.email}
                    data-testid={`customer-row-${c.email}`}
                    className="transition-colors hover:bg-white/[0.03]"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{c.name || '—'}</div>
                      <div className="text-xs text-white/50">{c.email}</div>
                    </td>
                    <td className="hidden max-w-xs px-4 py-3 text-white/70 md:table-cell">
                      <div className="line-clamp-2">
                        {(c.products || []).join(', ') || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {c.total_orders > 1 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#FF6B6B]/15 px-2 py-0.5 text-xs font-medium text-[#FF6B6B]">
                          <Repeat className="h-3 w-3" />
                          {c.total_orders}
                        </span>
                      ) : (
                        <span className="text-white/70">{c.total_orders}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-[#00FF94]">
                      {fmtUsd(c.lifetime_value)}
                    </td>
                    <td className="hidden px-4 py-3 text-white/60 sm:table-cell">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {fmtDate(c.first_purchase)}
                      </span>
                    </td>
                    <td className="hidden px-4 py-3 text-white/60 sm:table-cell">
                      {fmtDate(c.last_purchase)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a
                        data-testid={`customer-email-link-${c.email}`}
                        href={`mailto:${c.email}?subject=Quick%20update%20about%20your%20purchase`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#00F0FF] hover:text-[#00F0FF]/80"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Email
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const SummaryCard = ({ icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="rounded-xl border border-white/10 bg-neutral-900 p-4"
  >
    <div className={`mb-1 flex items-center gap-2 text-xs ${accent}`}>
      {icon}
      <span className="uppercase tracking-wider text-white/50">{label}</span>
    </div>
    <div className="text-xl font-semibold text-white md:text-2xl">{value}</div>
  </div>
);

const Th = ({ children, className = '' }) => (
  <th className={`px-4 py-3 text-left font-medium ${className}`}>{children}</th>
);
